import { useState } from "react";

import AssessmentApi from "@/api/assessment-api";
import { ACTION, type AssessmentGridAction } from "@/components/assessment-grid/constants";
import Confirm from "@/components/common/confirm";
import ContextMenu, { type ContextMenuOption } from "@/components/context-menu";
import AssessmentStepForm from "@/components/forms/assessment-step";
import AssessorForm from "@/components/forms/assessor";
import UserAvatar from "@/components/user-avatar";
import { cn, logRejection } from "@/helpers";
import type { HandleResponseReturn } from "@/hooks/request";
import { showSnackbar } from "@/hooks/snackbar";
import type { AssessmentStep, Assessor } from "@/types/assessment";

type Parent = {
  assignment_id: string;
  assessors: Assessor[];
};

interface AssessmentStepUtilProps {
  parent: Parent;
  step: AssessmentStep;
  active: boolean;
  is_first: boolean;
  is_last: boolean;
  dispatch: React.Dispatch<AssessmentGridAction>;
}

export default function AssessmentStepUtil({
  parent,
  step,
  active,
  is_first,
  is_last,
  dispatch,
}: AssessmentStepUtilProps) {
  const [shared_abort_controller, _] = useState(new AbortController());
  const _addAssessor = (data: Assessor | null) => {
    if (!data) return;
    dispatch({ type: ACTION.update_assessors, payload: data });
  };

  const _removeAssessor = ({ email }: { email: string }) => {
    AssessmentApi.removeAssessmentUser(
      email,
      parent.assignment_id,
      step.id,
      shared_abort_controller.signal,
    )
      .then(({ error }) => {
        if (error) {
          console.error(error);
          // Confirm closes either way, so the row staying put is the only sign
          // of a failure without this.
          showSnackbar(`Could not remove ${email}.`, "warning");
          return;
        }
        dispatch({
          type: ACTION.remove_assessor,
          payload: { email, assessment_step_id: step.id },
        });
      })
      .catch(logRejection);
  };

  const _moveLeft = () => {
    AssessmentApi.changeAssessmentStepOrdering(step.id, false, shared_abort_controller.signal)
      .then(({ data, error }) => _handleMoved({ data, error }))
      .catch(logRejection);
  };

  const _moveRight = () => {
    AssessmentApi.changeAssessmentStepOrdering(step.id, true, shared_abort_controller.signal)
      .then(({ data, error }) => _handleMoved({ data, error }))
      .catch(logRejection);
  };

  const _handleMoved = ({ data, error }: HandleResponseReturn<AssessmentStep[]>) => {
    if (error) {
      console.error(error);
    } else if (data) {
      dispatch({ type: ACTION.add_assessment_steps, payload: data });
    }
  };

  const _remove = () => {
    AssessmentApi.removeAssessmentStep(step.id, shared_abort_controller.signal)
      .then(({ error }) => {
        if (error) {
          console.error(error);
        } else {
          dispatch({ type: ACTION.remove_assessment_step, payload: step.id });
        }
      })
      .catch(logRejection);
  };

  const _updateAssessmentStep = (name: string) => {
    AssessmentApi.updateAssessmentStep(
      {
        step_id: step.id,
        assignment_id: parent.assignment_id,
        title: name,
      },
      shared_abort_controller.signal,
    )
      .then(({ error, data }) => {
        if (error) {
          console.error(error);
        } else if (data) {
          dispatch({ type: ACTION.update_assessment_steps, payload: data });
        }
      })
      .catch(logRejection);
  };

  const context_options: ContextMenuOption[] = [
    {
      icon: "person_add",
      label: "Add assessor",
      form: (
        <AssessorForm
          assignment_id={parent.assignment_id}
          assessment_step_id={step.id}
          callbackFn={_addAssessor}
        />
      ),
    },
    {
      icon: "edit",
      label: "Change step name",
      form: <AssessmentStepForm name={step.title} callbackFn={_updateAssessmentStep} />,
    },
  ];
  if (!is_first) {
    context_options.push({
      icon: "arrow_back_ios_new",
      label: "Move left",
      onClick: _moveLeft,
    });
  }
  if (!is_last) {
    context_options.push({
      icon: "arrow_forward_ios",
      label: "Move right",
      onClick: _moveRight,
    });
  }
  context_options.push({
    icon: "delete",
    label: "Delete",
    onClick: _remove,
  });

  return (
    <div key={step.id} className="flex h-[7rem] w-[12rem] flex-col items-center text-center">
      <div className="flex items-center">
        <span className={cn({ "font-bold": !active, "text-sm": active })}>{step.title}</span>
        {active && (
          <span className="ml-2">
            <ContextMenu options={context_options} />
          </span>
        )}
      </div>

      <div className="group mt-2 flex flex-row">
        {parent.assessors
          ?.filter((assessor) => step.id === assessor.assessment_step_id)
          .map((assessor) => {
            return (
              <div
                key={assessor.email}
                className="transition-margin -ml-2 text-[10px] group-hover:ml-1 first:ml-0"
              >
                <UserAvatar user={assessor} />
                {active && (
                  <Confirm
                    body={`Removing ${assessor.email}`}
                    fn_args={{
                      email: assessor.email,
                    }}
                    confirmFn={_removeAssessor}
                  >
                    <span
                      className="material-symbols-rounded invisible cursor-pointer text-slate-600 group-hover:visible hover:text-almost-white"
                      data-email={assessor.email}
                    >
                      delete
                    </span>
                  </Confirm>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
